import { Injectable } from '@angular/core';
import { Coordinate } from '@models/coordinate';
import { CDeploymentPoint } from '@models/imported/SagaSchema/CDeploymentPoint';
import { GroupName, SagaLayerManager } from '@techwan/ionic-core/map';
import { ExtendedLayer, LayerFactory, Map as SagaMap } from '@techwan/mapping';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LocalizePositionService } from '../../../core/map/services/localize-position/localize-position.service';
import { DeploymentFeatureFactoryService } from '../deployment-feature-factory/deployment-feature-factory.service';
import { IDeploymentLayerController } from '../executables/deployment-layers-controller/deployment-layers-controller.service';
import { MyDeploymentService } from '../executables/my-deployment/my-deployment.service';

const MY_DEPLOYMENT_NAME = 'MyDeployment';

@Injectable()
export class MyDeploymentLayerService implements IDeploymentLayerController {
  private map: SagaMap;
  private myDeploymentLayer: ExtendedLayer = null;

  private _active: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get $active(): Observable<boolean> {
    return this._active as Observable<boolean>;
  }

  private _visible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get $visible(): Observable<boolean> {
    return this._visible as Observable<boolean>;
  }

  private subs: Subscription[] = [];

  constructor(
    private myDeployment: MyDeploymentService,
    private deploymentPointFactory: DeploymentFeatureFactoryService,
    private localizePosition: LocalizePositionService
  ) {}

  onMapAdded(map: SagaMap) {
    this.subs.push(this.deploymentPointFactory.setup().subscribe(() => this.init(map)));
  }

  onMapRemoved() {
    this.cleanup();
  }

  private cleanup() {
    while (this.subs.length > 0) {
      this.subs.pop().unsubscribe();
    }
    if (this.myDeploymentLayer) {
      this.myDeploymentLayer.un('change:visible', this.onChangeVisible, this);
      this.myDeploymentLayer = null;
    }
    this.map = null;
  }

  private init(map: SagaMap) {
    this.map = map;
    const sagaLayerManager = map.getLayerManager() as SagaLayerManager;

    this.myDeploymentLayer = this.createMyDeploymentLayer(map);
    sagaLayerManager.setAutohide(this.myDeploymentLayer);
    this.myDeploymentLayer.on('change:visible', this.onChangeVisible, this);

    const deploymentLayersGroup = sagaLayerManager.getLayerGroup(GroupName.deploymentLayers);
    deploymentLayersGroup.getLayers().insertAt(0, this.myDeploymentLayer);

    this.subs.push(this.myDeployment.$myDeploymentPoint.subscribe(deploymentPoint => this.onMyDeploymentChanged(deploymentPoint)));
  }

  private onChangeVisible(event: ol.ObjectEvent) {
    this._visible.next(this.myDeploymentLayer.getVisible());
  }

  private createMyDeploymentLayer(map: SagaMap): ExtendedLayer {
    const layerFactory = new LayerFactory();
    layerFactory.map = map;
    let myDeploymentLayer = layerFactory.createLayer({ id: GroupName.showMyDeployment, cluster: false });
    myDeploymentLayer.set('name', MY_DEPLOYMENT_NAME);
    myDeploymentLayer.setZIndex(2);

    return myDeploymentLayer;
  }

  private onMyDeploymentChanged(deploymentPoint: CDeploymentPoint): void {
    this.refreshMyDeploymentPoint();
    this._active.next(deploymentPoint !== null);
    this._visible.next(this.myDeploymentLayer.getVisible());
  }

  private refreshMyDeploymentPoint() {
    const source = this.myDeploymentLayer.getSource();
    source.clear();

    if (this.myDeployment.mine !== null) {
      source.addFeature(this.deploymentPointFactory.fromDeployment(this.myDeployment.mine, this.map, true));
      source.changed();
    }
  }

  showDeploymentPoint(show: boolean): void {
    if (this.myDeploymentLayer !== null) {
      this.myDeploymentLayer.setVisible(show);
      if (show) {
        this.localizePosition.centerView(this.map.getView(), this.getMyDeploymentCoordinates());
      }
    }
  }

  private getMyDeploymentCoordinates(): Coordinate {
    const mine = this.myDeployment.mine;
    return { x: mine.CenterX, y: mine.CenterY, epsg: mine.CoordEpsgCode };
  }
}
